import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import get from 'lodash/get';
import {
    createAccount as createAccountApi,
    getAccountByEmail,
    enrollReward,
} from 'yoda-interfaces/lib/Account/AccountApi';
import { createAccount } from 'yoda-site-components/lib/actions/NativeAppAction';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import {
    getAccertifyDeviceInfo,
    logExtendedAnalytics,
} from 'yoda-site-components/lib/sagas/SiteProbingSaga';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import { getURLParameterBykey } from 'yoda-core-components/lib/helpers/Utils/Utils';
import * as AccountActionTypes from 'yoda-site-components/lib/actionTypes/AccountActionTypes';
import * as checkoutAnalyticsAction from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import {
    ANALYTICS_ACTIONS_CREATE_ACCOUNT,
    ANALYTICS_ACTIONS_LINK_REWARDS_ACCOUNT,
} from 'yoda-checkout-components/lib/common/Constants';
import AnalyticsActionTypes from 'yoda-site-components/lib/actionTypes/AnalyticsActionTypes';
import { ANALYTICS_SRC_CODE } from 'yoda-site-components/lib/common/Constants';
import * as CreateAccountPostOrderActionTypes from '../actionTypes/CreateAccountPostOrderActionTypes';

function* nativeCreateAccount(data) {
    const {
        body: {
            firstName,
            lastName,
            emailAddress,
            password,
            expressCheckout,
            rewardsProfile: {
                contact: { phone },
            },
        },
        orderId,
    } = data;
    yield put(
        createAccount({
            firstName,
            lastName,
            emailAddress,
            password,
            expressCheckout,
            phone,
            orderId,
        })
    );
}

function* onCreateAccount({ isSuccess = false, response = {} }, isNotNative = false) {
    /*  Trigger ADI analytics only if we find accertify flag enabled and
        accertify header present as part of response */
    const appContext = yield select((state) => state.context);
    const { enableAccertifyADI } = (appContext || {}).featureFlags || {};
    const accertifyStatus = response?.headers?.get('x-adi-sts');
    if (enableAccertifyADI && ['A', 'R'].includes(accertifyStatus)) {
        yield call(logExtendedAnalytics, {
            actionMethod: 'OCP_CREATE_ACCOUNT',
            result: { response },
        });
    }
    const isMonitoringMode = response?.headers?.get('x-adi-monitor');
    /*  formError event was already triggered from inside logExtendedAnalytics
        in the case of enforced ADI Reject. */
    const isFormErrorEventTriggered =
        enableAccertifyADI && accertifyStatus === 'R' && !isMonitoringMode;
    if (!isSuccess && !isFormErrorEventTriggered) {
        const errorType = response.data?.errorCode === 'GENERIC_ERROR' ? 'SYSTEM' : 'BUSINESS';
        const src = ANALYTICS_SRC_CODE[`OCP_CREATE_ACCOUNT_${errorType}`];
        yield put({
            type: AnalyticsActionTypes.FORM_ERROR,
            errorDetails: {
                error: { errorDescription: response.data?.errorMessage },
                fraudForceErrData: { src },
            },
        });
    }
    if (isSuccess) {
        TokenProvider.set('DP_REWARDS_STATUS', 'Enrolled');
        response.userState = '1';
        response.status = 'SUCCESS';
        isNotNative && TokenProvider.storeTokens(response, 'header');
        yield all([
            put({
                type: CreateAccountPostOrderActionTypes.CREATE_ACCOUNT_POST_REQUEST_SUCCESS,
                createAccountPostOrderData: response,
            }),
            put({
                type: AccountActionTypes.GET_SITE_USER_PROFILE,
            }),
            put(
                checkoutAnalyticsAction.clickEventAnalyticsAction(ANALYTICS_ACTIONS_CREATE_ACCOUNT)
            ),
            put(
                checkoutAnalyticsAction.clickEventAnalyticsAction(
                    ANALYTICS_ACTIONS_LINK_REWARDS_ACCOUNT
                )
            ),
        ]);
    } else {
        response.status = 'FAILED';
        yield put({
            type: CreateAccountPostOrderActionTypes.CREATE_ACCOUNT_POST_REQUEST_ERROR,
            createAccountPostOrderError: response,
        });
    }
}

// eslint-disable-next-line no-unused-vars
function* onNativeCreateAccount({ payload }) {
    // eslint-disable-next-line no-param-reassign
    payload = JSON.parse(payload, '{}');
    yield call(onCreateAccount, payload, false);
}

function* onNativeCreateAccountFailure({ payload }) {
    const payloadParam = payload;
    payloadParam.response.status = 'FAILED';
    yield put({
        type: CreateAccountPostOrderActionTypes.CREATE_ACCOUNT_POST_REQUEST_ERROR,
        createAccountPostOrderError: payloadParam.response,
    });
}

function* createAccountPostOrderSaga(action) {
    try {
        const postOrderData = (state) => state.createAccountPostOrderData;
        const data = yield select(postOrderData);
        const isNative = yield select((state) => get(state, 'context.isNative', false));
        const modifyAction = action;
        /* Do not send deviceInfo incase of Native Apps */
        if (!isNative) {
            const deviceInfo = yield call(getAccertifyDeviceInfo, {
                actionName: 'Checkout_create_account_OCP',
            });
            if (deviceInfo) {
                modifyAction.payload.body.deviceInfo = deviceInfo;
            }
        }
        modifyAction.payload.orderId = getURLParameterBykey('OrderId', window.location.href);
        modifyAction.payload.orderSource = data.orderSource;
        modifyAction.payload.xSessionId = data.xSessionId;
        modifyAction.headers = {
            userAction: 'SEARCH_AND_LINK',
        };

        if (isNative) {
            yield call(nativeCreateAccount, modifyAction.payload);
        } else {
            const createAccountPostOrderData = yield call(
                FactorySaga,
                createAccountApi,
                modifyAction
            );
            yield call(onCreateAccount, createAccountPostOrderData, true);
        }
    } catch (createAccountPostOrderError) {
        yield put({
            type: CreateAccountPostOrderActionTypes.CREATE_ACCOUNT_POST_REQUEST_ERROR,
            createAccountPostOrderError,
        });
    }
    yield put(showGlobalLoader(false));
}

function* getAccountByEmailSaga(action) {
    yield put({
        type: CreateAccountPostOrderActionTypes.CHECK_ACCOUNT_API_CALLED,
        value: true,
    });
    let isAccountExists = false;
    try {
        const emailId = action.payload;
        const encodeEmail = window.btoa(emailId);
        const response = yield call(getAccountByEmail, encodeEmail);
        if (response && response.data) {
            isAccountExists = response.data.exists;
            yield put({
                type: CreateAccountPostOrderActionTypes.IS_EMAIL_ACCOUNT_EXIST,
                isAccountExists,
            });
        }
    } catch (error) {
        yield put({
            type: CreateAccountPostOrderActionTypes.IS_EMAIL_ACCOUNT_EXIST,
            isAccountExists,
        });
    }
}

export function* enrollRewardlinkSaga(action) {
    try {
        // eslint-disable-next-line no-param-reassign
        action.headers = {
            userAction: 'SIGN_IN_AND_ENROLL',
        };
        const response = yield call(FactorySaga, enrollReward, action);
        const enrollRewardsData = response.response;
        if (response.isSuccess) {
            enrollRewardsData.status = 'SUCCESS';
            yield all([
                put({
                    type: CreateAccountPostOrderActionTypes.ENROLL_REWARD_LINK_SUCCESS,
                    enrollRewardsData,
                }),
                put(
                    checkoutAnalyticsAction.clickEventAnalyticsAction(
                        ANALYTICS_ACTIONS_LINK_REWARDS_ACCOUNT
                    )
                ),
            ]);
            TokenProvider.set('DP_REWARDS_STATUS', 'Enrolled');
        } else {
            enrollRewardsData.status = 'FAILED';
            yield put({
                type: CreateAccountPostOrderActionTypes.ENROLL_REWARD_LINK_ERROR,
                enrollRewardsData,
            });
        }
    } catch (enrollRewardsError) {
        yield put({
            type: CreateAccountPostOrderActionTypes.ENROLL_REWARD_LINK_ERROR,
            enrollRewardsError,
        });
    }
    yield put(showGlobalLoader(false));
}

const watchCreateAccountPostOrder = function* watchCreateAccountPostOrder() {
    yield takeLatest(
        CreateAccountPostOrderActionTypes.CREATE_ACCOUNT_POST_REQUEST,
        createAccountPostOrderSaga
    );
};
watchCreateAccountPostOrder.sagaName = 'watchCreateAccountPostOrder';

const watchGetAccountByEmail = function* watchGetAccountByEmail() {
    yield takeLatest(CreateAccountPostOrderActionTypes.GET_ACCOUNT_BY_EMAIL, getAccountByEmailSaga);
};
watchGetAccountByEmail.sagaName = 'watchGetAccountByEmail';

const watchNativeCreateAccountFailure = function* watchNativeCreateAccountFailure() {
    yield takeLatest('WEB_CREATE_ACCOUNT_COMPLETED_REQUEST', onNativeCreateAccountFailure);
};
watchNativeCreateAccountFailure.sagaName = 'watchNativeCreateAccountFailure';

const watchEnrollRewardlink = function* watchEnrollRewardlink() {
    yield takeLatest(CreateAccountPostOrderActionTypes.ENROLL_REWARD_LINK, enrollRewardlinkSaga);
};
watchEnrollRewardlink.sagaName = 'watchEnrollRewardlink';

export {
    watchCreateAccountPostOrder,
    watchGetAccountByEmail,
    watchEnrollRewardlink,
    watchNativeCreateAccountFailure,
};
